import type { MessageDescriptor } from 'react-intl'
import { defineMessages } from 'react-intl'
import { createSelector } from 'reselect'
import { checkoutAccommodationOfferView } from './view/accommodation'
import { OFFER_TYPE_CRUISE, OFFER_TYPE_TOUR_V2, OFFER_TYPE_VILLA } from 'constants/offer'
import config from 'constants/config'
import { getCustomOfferItems } from './view/customOffer'
import { getVillaViews } from './view/villa'
import { getBundleAndSaveItems } from './view/bundleAndSave'
import { isCruiseOfferView } from 'checkout/lib/utils/cruises/view'
import { getCarHireItemsView } from './view/carHire'
import { cartIncludesFlights, checkoutContainsFlightsWithEmbeddedInsurance, getFlightItems, getFlightItemsView, isStandaloneFlights } from './view/flights'
import { getGiftCardItems } from './view/giftCard'
import { getPurchaseCreditItems } from './view/purchaseCredit'
import isInsuranceSupported from 'checkout/selectors/request/isInsuranceSupported'
import isBookingProtectionEnabled from 'checkout/selectors/request/isBookingProtectionEnabled'
import { shouldShowArrivalDetailsForm, shouldShowPaymentForm, shouldShowTravellerForm } from './stepsSelectors'
import { isBalancePending } from 'lib/payment/depositsUtils'
import {
  CHECKOUT_INSURANCE_CONTAINER_ID,
  CHECKOUT_EXPERIENCE_TRANSFER_CONTAINER_ID,
  CHECKOUT_REVIEW_CONTAINER_ID,
  CHECKOUT_TRAVELLER_CONTAINER_ID,
  CHECKOUT_ARRIVAL_DETAILS_CONTAINER_ID,
  CHECKOUT_PAYMENTS_CONTAINER_ID,
  CHECKOUT_BUNDLED_CAR_HIRE_CONTAINER_ID,
  CHECKOUT_MERCHANDISE_CONTAINER_ID,
  CHECKOUT_EXPERIENCE_LOUNGE_CONTAINER_ID,
} from 'constants/checkout'
import { getIsLuxPlusEnabled, getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { isStandaloneLuxPlusSubscription } from './view/luxPlusSubscription'
import getTourV2ExperienceItemsViews from 'checkout/selectors/view/itemViews/getTourV2ExperienceItemsViews'
import { isSpoofed, isTravelProtectionAvailableInRegion } from 'selectors/featuresSelectors'
import { isSimpleStandaloneCheckout, isStandaloneExperience, getCartId } from 'selectors/checkoutSelectors'
import { INSURANCE_TITLE_DESCRIPTOR, TRAVEL_PROTECTION_TITLE_DESCRIPTOR } from 'checkout/constants/insurance'
import { getExperienceOneClickTransferOptions, getFlightLoungePassExperienceAvailable, getOneClickExperienceTransfer, isOneClickExperienceTransferEnabled, getIsOneClickTransferAvailable } from './experienceSelectors'
import { isNonEmptyArray } from 'lib/array/arrayUtils'
import { findPostPurchaseCheckout, isAccommodationItem, isBedbankItem, isCarHireItem, isBookingProtectionItem, isInsuranceItem, isLuxPlusSubscriptionItem, isFlightItem, isSubscriptionJoinItem, isExperienceItem } from 'lib/checkout/checkoutUtils'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getCheckoutTravellerSchema from 'checkout/selectors/getCheckoutTravellerSchema'
import getCheckoutTotal from 'checkout/selectors/payment/getCheckoutTotal'
import { isCruiseCheckout } from './view/cruise'
import { isBundledCarHireEnabled, isCartEligibleForBundledCarHire } from 'selectors/carHireSelectors'
import getExperienceItemsView from './view/itemViews/getExperienceItemsView'
import getTransferItems from './view/items/getTransferItems'
import isInsuranceBookingProtectionPostPurchaseEnabled from 'selectors/isInsuranceBookingProtectionPostPurchaseEnabled'
import { isFlightWithEmbeddedInsurance } from 'checkout/utils'
import getMerchandiseItems from './view/items/getMerchandiseItems'
import { getCheckoutBenefitsView } from 'luxPlus/selectors/checkout/benefits'
import getCanRedeemCheckoutLoyaltyBenefits from './loyalty/getCanRedeemCheckoutLoyaltyBenefits'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { LOYALTY_BONUS_POINTS_CAMPAIGN_LABEL } from 'loyalty/constants/loyaltyBonusPoints'
import { getLoyaltyBonusPointsCampaignTypes } from 'loyalty/lib/determineLoyaltyBonusPointsCampaignType'
import { capitaliseAll } from 'lib/string/stringUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import getUnifiedTransferView from './view/itemViews/getUnifiedTransferView'
import { getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { OptimizelyExperiments } from 'constants/optimizely'
import { getInsuranceItems } from './view/insurance'
import { getBookingProtectionOccupantsFromCartItems } from './request/bookingProtection'
import { getFunnelFeatures } from './funnel'
import getIsInsurnaceDomesticTrip from './view/getIsInsurnaceDomesticTrip'
import getCheckoutInsuranceQuoteParams from './request/getCheckoutInsuranceQuoteParams'
import { getInsuranceProductListKey, getInsuranceQuoteKey } from 'lib/insurance/insuranceUtils'
import { getIsInternationalCheckoutFlowTreatment } from './internationalCheckoutFlow'
import { isLuxPlusAvailableToAddToCart } from './navigationSelectors'
import isPassportCardExclusiveCheckoutEnabled from './request/isPassportCardExclusiveCheckoutEnabled'

const sectionTitleMessages = defineMessages({
  reviewGift: {
    id: 'ecGGgBWnEDdTNfNh1GEL',
    defaultMessage: 'Let’s review your gift',
    description: 'Checkout review section title when the order is a gift',
  },
  reviewExperience: {
    id: 'hRQBXm0AhOY5-Yj24_Un',
    defaultMessage: 'Review your experience',
    description: 'Checkout review section title for standalone experience bookings',
  },
  reviewEscape: {
    id: 'Q4Z_iyuSeNzj0WYDrpqX',
    defaultMessage: 'Let’s review your escape',
    description: 'Default checkout review section title for travel bookings',
  },
  travellerCarHireDriver: {
    id: 'lolU7Es0iRykjHLUK8PN',
    defaultMessage: 'Who’s the driver?',
    description: 'Checkout traveller section title when booking is car hire only',
  },
  travellerDefault: {
    id: 'M44ETbZasQKv7SJGovxH',
    defaultMessage: 'Who’s going?',
    description: 'Default checkout traveller section title during checkout',
  },
  insuranceProtectFlights: {
    id: '9Q6clUe2_OG2ORoErAu4',
    defaultMessage: 'Protect yourself if you need to cancel your flights',
    description: 'Checkout insurance section title for standalone flight bookings',
  },
  insuranceFlightsProtectedCoverHotel: {
    id: '3QckaJ8TDYHURVos6fIo',
    defaultMessage: 'Your flights are protected — now cover your hotel too',
    description: 'Checkout insurance section title when all flights are protected but hotel is not',
  },
  insuranceSecureFullProtection: {
    id: '4Yj3mnqZxeIj769Xydgc',
    defaultMessage: 'Secure full protection — flights and hotel covered if plans change',
    description: 'Checkout insurance section title when partial flight protection exists',
  },
  insuranceFlightsAndAccommodationProtected: {
    id: 'Sx4w4JfrHPTvzfUCtUaz',
    defaultMessage: 'Your flights and accommodation are protected — secure full booking protection too',
    description: 'Checkout insurance section title when flights and accommodation are both protected',
  },
  insuranceHotelProtectedCoverFlights: {
    id: 'C-GMNykP00o-9IpKmQRV',
    defaultMessage: 'Your hotel is protected — now cover your flights too',
    description: 'Checkout insurance section title when hotel is protected but flights are not',
  },
  insuranceHotelProtectedSecureFull: {
    id: 'gWXcnvlWEDyKSlda-A_q',
    defaultMessage: 'Your hotel is protected — secure full protection',
    description: 'Checkout insurance section title when only hotel is protected and no flights in booking',
  },
  insuranceProtectBooking: {
    id: '9XrA6Zix7O3fqAZh27WG',
    defaultMessage: 'Protect yourself if you need to cancel your booking',
    description: 'Default checkout insurance section title for booking protection',
  },
  insuranceCruise: {
    id: 'kCE5pc6GE7RYtRXxL97o',
    defaultMessage: 'Want to add cruise protection?',
    description: 'Checkout insurance section title for cruise bookings',
  },
  passportCardInsurance: {
    id: 'NJPV7fFUvLI_ehJEKWX5',
    defaultMessage: 'PassportCard Comprehensive Travel Insurance',
    description: 'Checkout insurance section title for the exclusive PassportCard insurance checkout section',
  },
  bundledCarHireDiscount: {
    id: 't7nNwgKfzkVbRNSni3lu',
    defaultMessage: 'Unlock {discountPercentage}% off car hire for your stay',
    description: 'Checkout bundled car hire section title with discount percentage',
  },
  bundledCarHireExplore: {
    id: 'jVPC45OeouMBSZ90oZ1i',
    defaultMessage: 'Explore exclusive Car Hire offers for your stay',
    description: 'Checkout bundled car hire section title for objective intent messaging',
  },
  bundledCarHireLoyalty: {
    id: 'g9n_akIG3EGhtmnfMzNH',
    defaultMessage: 'Get exclusive {campaignLabel} offers on car hire for your stay',
    description: 'Checkout bundled car hire section title with loyalty bonus points campaign label',
  },
  experienceTransfer: {
    id: '8W2hnF9tqoT8iFsvZSrm',
    defaultMessage: 'Need airport transfers?',
    description: 'Checkout experience transfer section title during checkout',
  },
  experienceLounge: {
    id: 'uX71jW6xpdlPxHrTOrwM',
    defaultMessage: 'Turn wait time into downtime',
    description: 'Checkout experience lounge section title during checkout',
  },
  arrivalDetails: {
    id: 'tOvUmiZ7GH9XDj9SmYFu',
    defaultMessage: 'When will you be arriving?',
    description: 'Checkout arrival details section title during the checkout and payment process',
  },
  paymentHow: {
    id: 'IEmfEWYQqPYFbvKFAFlL',
    defaultMessage: 'How would you like to pay?',
    description: 'Checkout payment section title during the checkout and payment process',
  },
  merchandiseReview: {
    id: '93Jj2baqfsLcDgCPdjWM',
    defaultMessage: 'Let’s review your shop order',
    description: 'Checkout merchandise section title during the checkout and payment process',
  },
})

export type CheckoutSectionTitle =
  | { kind: 'descriptor'; descriptor: MessageDescriptor; values?: Record<string, string | number> }
  | { kind: 'literal'; text: string }

export interface CheckoutNumberedSection<T> {
  id: string
  title?: CheckoutSectionTitle
  checkoutNumber?: string
  data: T
}

export type CheckoutSectionsReview = 'customOffer' | 'accommodation' | 'villa' | 'bundleAndSave' | 'cruise' | 'carHire' | 'tour' | 'tourExperience' | 'flight' | 'flightDateChange' | 'experience' | 'reviewTransfer' | 'giftCard' | 'purchaseCredit' | 'customerServicePanel' | 'luxPlusInlineToggle' | 'luxPlusInflow' | 'luxPlusReconsider' | 'luxPlusStandalone' | 'loyaltyBenefits' | 'loyalty' | 'charterUpgrade'
export type CheckoutSectionsExperienceTransfer = 'experienceTransfer'
export type CheckoutSectionsExperienceLounge = 'experienceLounge'
export type CheckoutSectionsInsurance = 'insurance' | 'bookingProtection' | 'spoofedInsurance' | 'passportCardInsurance'
export type CheckoutSectionsCarHire = 'bundledCarHire'
export type CheckoutSectionsTravellerDetails = 'customOfferTravellers' | 'travellerDetails' | 'flightServices' | 'carHireArrivalFlightNumber' | 'agentBookingDetails' | 'depositDetailsNotice' | 'paymentScheduleDetailsNotice'
export type CheckoutSectionsArrivalDetails = 'arrivalDetails'
export type CheckoutSectionsHighValue = 'loyalty'
export type CheckoutSectionsPayment = 'instalmentBalancePaymentSchedule' | 'paymentForm'
export type CheckoutSectionsMerchandise = 'merchandise' | 'merchandiseShipping'

export interface CheckoutSections {
  review: CheckoutNumberedSection<Array<CheckoutSectionsReview>>
  experienceTransfer: CheckoutNumberedSection<Array<CheckoutSectionsExperienceTransfer>>
  experienceLounge: CheckoutNumberedSection<Array<CheckoutSectionsExperienceLounge>>
  insurance: CheckoutNumberedSection<Array<CheckoutSectionsInsurance>>
  bundledCarHire: CheckoutNumberedSection<Array<CheckoutSectionsCarHire>>
  travellerDetails: CheckoutNumberedSection<Array<CheckoutSectionsTravellerDetails>>
  arrivalDetails: CheckoutNumberedSection<Array<CheckoutSectionsArrivalDetails>>
  payment: CheckoutNumberedSection<Array<CheckoutSectionsPayment>>
  merchandise: CheckoutNumberedSection<Array<CheckoutSectionsMerchandise>>
}

const activeSections = <T>(sections: Array<[T, boolean]>) => sections.filter(section => Boolean(section[1])).map(section => section[0])

// @TODO: a 'cruise' is not an accommodation item
export const filterCheckoutAccommodationView = (view: App.Checkout.AccommodationOfferView) => {
  return view.offerType !== OFFER_TYPE_CRUISE && view.offerType !== OFFER_TYPE_TOUR_V2 && view.offerType !== OFFER_TYPE_VILLA
}

const accommodationViews = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  (offerViews) => offerViews.data.filter(filterCheckoutAccommodationView),
)

export const hasBundledAccommodationRate = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => {
    const bundledAccommodationIds = new Set<string>()
    for (const item of items) {
      if (isAccommodationItem(item) && 'isAccomBundle' in item && !!item.isAccomBundle) {
        bundledAccommodationIds.add(item.isAccomBundle)
      }
    }

    if (!bundledAccommodationIds.size) {
      return false
    }

    return items.some(item =>
      isExperienceItem(item) &&
      !!item.isAccomBundle &&
      bundledAccommodationIds.has(item.isAccomBundle),
    )
  },
)

const hasBookingProtectionInsuranceFetchError = createSelector(
  (state: App.State) => getIsInsurnaceDomesticTrip(state),
  (state: App.State) => isCruiseCheckout(state),
  (state: App.State) => getCheckoutInsuranceQuoteParams(state),
  (state: App.State) => state.insurance.productLists,
  (state: App.State) => state.insurance.productListErrors,
  (state: App.State) => state.insurance.fetchQuotesErrors,
  (
    isDomestic,
    isCruise,
    quoteParams,
    productLists,
    productListErrors,
    fetchQuotesErrors,
  ): boolean => {
    // If either product lookup or the primary quote lookup fails, hide booking protection entirely.
    const productListKey = getInsuranceProductListKey('booking-protection', {
      isDomestic,
      isCruise,
    })

    if (productListErrors[productListKey]) return true

    const bookingProtectionProductId = productLists[productListKey]?.[0]
    if (!bookingProtectionProductId) return false

    const quoteKey = getInsuranceQuoteKey(bookingProtectionProductId, {
      ...quoteParams,
      type: 'booking-protection',
      isCruise,
    })

    return !!fetchQuotesErrors[quoteKey]
  },
)

export const checkoutShouldShowBookingProtection = createSelector(
  (state: App.State) => isBookingProtectionEnabled(state),
  (state: App.State) => getBookingProtectionOccupantsFromCartItems(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => isInsuranceBookingProtectionPostPurchaseEnabled(state),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getOptimizelyExperimentVariation(state, OptimizelyExperiments.croFlexCfmrBedbankPackages),
  (state: App.State) => checkoutContainsFlightsWithEmbeddedInsurance(state),
  (state: App.State) => getFlightItems(state),
  (state: App.State) => hasBookingProtectionInsuranceFetchError(state),
  (
    enabledForCartItems,
    bookingProtectionOccupants,
    postPurchase,
    isPostPurchaseEnabled,
    checkoutItems,
    isCroFlexCfmrBedbankPackagesEnabled,
    hasProtectedFlights,
    flights,
    hasInsuranceFetchError,
  ): boolean => {
    if (hasInsuranceFetchError) return false

    if (postPurchase) {
      return isPostPurchaseEnabled
    }

    // If the occupants are inconsistent, we do not display booking protection
    if (!bookingProtectionOccupants) return false

    // If the CFMR flex package experiment is not enabled, fall back to standard logic
    if (!isCroFlexCfmrBedbankPackagesEnabled) return enabledForCartItems

    // Check if cart contains bedbank items and the flex package experiment is active
    const hasBedbankAndFlexPackageIsEnabled = checkoutItems.some(item => isBedbankItem(item)) && !!isCroFlexCfmrBedbankPackagesEnabled

    // Determine if this is a standalone bedbank checkout (only bedbank + optional protection/insurance/luxplus items)
    const isStandaloneBedbankCheckout = checkoutItems.length > 0
      && checkoutItems.every(item => isBedbankItem(item) || isBookingProtectionItem(item) || isInsuranceItem(item) || isLuxPlusSubscriptionItem(item) || isSubscriptionJoinItem(item))
      && checkoutItems.some(item => isBedbankItem(item))

    // For standalone bedbank checkouts with flex package enabled,
    // hide booking protection (it's replaced by the flex package option)
    if (isStandaloneBedbankCheckout && hasBedbankAndFlexPackageIsEnabled) {
      return false
    }

    // Check if cart contains only bedbank accommodations and flights (plus optional items)
    const containsOnlyBedbankAndFlights = checkoutItems.every(item => isBedbankItem(item) || isInsuranceItem(item) || isLuxPlusSubscriptionItem(item) || isFlightItem(item))

    // Special case: bedbank + flights with embedded insurance + flex package enabled
    if (hasProtectedFlights && hasBedbankAndFlexPackageIsEnabled && containsOnlyBedbankAndFlights) {
      const flightItem = flights[0]
      const securedFlights = flightItem?.flights.filter(isFlightWithEmbeddedInsurance)

      // If ALL flights have embedded insurance, hide booking protection
      // (accommodation is covered by flex package, flights already protected)
      if (securedFlights.length === flightItem.flights.length) {
        return false
      }
    }

    // Default to the standard booking protection enablement logic
    return enabledForCartItems
  },
)

const checkoutShouldShowCarHireArrivalInput = createSelector(
  (state: App.State) => getCarHireItemsView(state),
  (carHireViews) => !!carHireViews.data.length && (carHireViews.data[0].item.isPickUpAtAirport ||
    carHireViews.data[0]?.pickUpLocation.name.toUpperCase().includes('AIRPORT')),
)

const getReviewSectionTitle = createSelector(
  (state: App.State) => isSimpleStandaloneCheckout(state),
  (state: App.State) => isStandaloneExperience(state),
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (simpleStandaloneCheckout, standaloneExperience, isGift, postPurchase): CheckoutSectionTitle | undefined => {
    if (postPurchase === 'charter-upgrade') {
      return undefined
    }
    if (isGift) {
      return { kind: 'descriptor', descriptor: sectionTitleMessages.reviewGift }
    } else if (simpleStandaloneCheckout) {
      return undefined
    } else if (standaloneExperience) {
      return { kind: 'descriptor', descriptor: sectionTitleMessages.reviewExperience }
    } else {
      return { kind: 'descriptor', descriptor: sectionTitleMessages.reviewEscape }
    }
  },
)

const getTravellerSectionTitle = createSelector(
  (state: App.State) => getCheckoutTravellerSchema(state),
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => state.checkout.cart.items,
  (formSchema, isGift, cartItems): CheckoutSectionTitle | undefined => {
    if (cartItems.length > 0 && cartItems.every(isCarHireItem)) return { kind: 'descriptor', descriptor: sectionTitleMessages.travellerCarHireDriver }
    if (isGift) {
      const recipientTitle = formSchema?.accommodationFlightSchema?.properties?.recipient?.title
      if (!recipientTitle) return undefined
      return { kind: 'literal', text: recipientTitle }
    }
    return { kind: 'descriptor', descriptor: sectionTitleMessages.travellerDefault }
  },
)

const getInsuranceSectionTitle = createSelector(
  (state: App.State) => isPassportCardExclusiveCheckoutEnabled(state),
  (state: App.State) => checkoutShouldShowBookingProtection(state),
  (state: App.State) => isInsuranceSupported(state),
  (state: App.State) => isSpoofed(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => isTravelProtectionAvailableInRegion(state),
  (state: App.State) => isCruiseCheckout(state),
  (state: App.State) => getFlightItems(state),
  (state: App.State) => getInsuranceItems(state),
  (
    shouldShowPassportCardInsurance,
    showBookingProtection,
    isInsuranceSupported,
    isSpoofed,
    standaloneFlights,
    isTravelProtection,
    hasCruiseItems,
    flights,
    insuranceItems,
  ): CheckoutSectionTitle | undefined => {
    if (isSpoofed) return undefined

    if (shouldShowPassportCardInsurance) {
      return { kind: 'descriptor', descriptor: sectionTitleMessages.passportCardInsurance }
    }

    // Booking protection flow - provides contextual messaging based on existing coverage
    if (showBookingProtection) {
      // Simple case: standalone flights only
      if (standaloneFlights) {
        return { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceProtectFlights }
      }

      // Analyze existing protection coverage for mixed bookings
      const flightItem = flights[0]
      const securedFlights = flightItem?.flights.filter(isFlightWithEmbeddedInsurance)

      const insuranceItem = insuranceItems[0]
      // Check if accommodation is already protected (via CFMR flex package)
      const isAccommodationProtected = !!insuranceItem?.bedbankRoomIds?.length || !!insuranceItem?.isAccommodationBundle

      // Case 1: Some/all flights protected, but accommodation is not
      if (securedFlights?.length && !isAccommodationProtected) {
        return securedFlights.length === flightItem.flights.length
          ? { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceFlightsProtectedCoverHotel }
          : { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceSecureFullProtection }
      }

      // Case 2: Some/all flights protected AND accommodation is protected
      if (securedFlights?.length && isAccommodationProtected) {
        return securedFlights.length === flightItem.flights.length
          ? { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceFlightsAndAccommodationProtected }
          : { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceSecureFullProtection }
      }

      // Case 3: Accommodation protected but flights are not (and flights exist in booking)
      if (isAccommodationProtected && !securedFlights?.length && !!flightItem) {
        return { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceHotelProtectedCoverFlights }
      }

      // Case 4: Only accommodation is protected (no flights in booking)
      if (isAccommodationProtected) {
        return { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceHotelProtectedSecureFull }
      }

      // Default case: no existing protection
      return { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceProtectBooking }
    }
    if (isInsuranceSupported && hasCruiseItems) return { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceCruise }
    if (isInsuranceSupported) return { kind: 'descriptor', descriptor: isTravelProtection ? TRAVEL_PROTECTION_TITLE_DESCRIPTOR : INSURANCE_TITLE_DESCRIPTOR }

    return undefined
  },
)

const getBundledCarHireSectionTitle = createSelector(
  (state: App.State) => state.carHire.bundleEligibilityCartList[getCartId(state)],
  (state: App.State) => getOptimizelyExperimentVariation(state, OptimizelyExperiments.carHireObjectiveIntentMessage),
  (bundleEligibilityForCart, isObjectiveIntentMessage): CheckoutSectionTitle | undefined => {
    if (!bundleEligibilityForCart?.isEligible) return undefined
    if (bundleEligibilityForCart.benefits?.has('discount')) {
      return {
        kind: 'descriptor',
        descriptor: sectionTitleMessages.bundledCarHireDiscount,
        values: { discountPercentage: bundleEligibilityForCart.discountPercentage ?? 0 },
      }
    }
    if (bundleEligibilityForCart.benefits?.has('loyalty_bonus_points')) {
      const bonusPointsCampaigns = getLoyaltyBonusPointsCampaignTypes({
        bonusPoints: bundleEligibilityForCart.loyaltyBonusPointsMultiplier ?? 0,
        basePoints: 1,
        bonusStatusCredits: 0,
        statusCredits: 0,
      })
      const bonusPointsCampaign = bonusPointsCampaigns[0]
      if (!bonusPointsCampaign) return undefined
      const bonusPointsCampaignLabel = getCurrentIntl().formatMessage(LOYALTY_BONUS_POINTS_CAMPAIGN_LABEL[bonusPointsCampaign])

      if (isObjectiveIntentMessage) {
        return { kind: 'descriptor', descriptor: sectionTitleMessages.bundledCarHireExplore }
      }
      return {
        kind: 'descriptor',
        descriptor: sectionTitleMessages.bundledCarHireLoyalty,
        values: { campaignLabel: capitaliseAll(bonusPointsCampaignLabel) },
      }
    }
    return undefined
  },
)

function getNumberedSections({
  reviewSections,
  experienceTransferSection,
  experienceLoungeSection,
  insuranceSections,
  bundledCarHireSection,
  travellerSections,
  arrivalDetails,
  paymentSections,
  merchandiseSections,
}: {
  reviewSections: Array<CheckoutSectionsReview>
  insuranceSections: Array<CheckoutSectionsInsurance>
  bundledCarHireSection: Array<CheckoutSectionsCarHire>
  travellerSections: Array<CheckoutSectionsTravellerDetails>
  arrivalDetails: Array<CheckoutSectionsArrivalDetails>
  paymentSections: Array<CheckoutSectionsPayment>
  experienceTransferSection: Array<CheckoutSectionsExperienceTransfer>
  experienceLoungeSection: Array<CheckoutSectionsExperienceLounge>
  merchandiseSections: Array<CheckoutSectionsMerchandise>
}): CheckoutSections {
  const sections = [
    { name: 'review', id: CHECKOUT_REVIEW_CONTAINER_ID, data: reviewSections },
    { name: 'experienceTransfer', id: CHECKOUT_EXPERIENCE_TRANSFER_CONTAINER_ID, data: experienceTransferSection },
    { name: 'experienceLounge', id: CHECKOUT_EXPERIENCE_LOUNGE_CONTAINER_ID, data: experienceLoungeSection },
    { name: 'insurance', id: CHECKOUT_INSURANCE_CONTAINER_ID, data: insuranceSections },
    { name: 'bundledCarHire', id: CHECKOUT_BUNDLED_CAR_HIRE_CONTAINER_ID, data: bundledCarHireSection },
    { name: 'travellerDetails', id: CHECKOUT_TRAVELLER_CONTAINER_ID, data: travellerSections },
    { name: 'arrivalDetails', id: CHECKOUT_ARRIVAL_DETAILS_CONTAINER_ID, data: arrivalDetails },
    { name: 'payment', id: CHECKOUT_PAYMENTS_CONTAINER_ID, data: paymentSections },
    { name: 'merchandise', id: CHECKOUT_MERCHANDISE_CONTAINER_ID, data: merchandiseSections },
  ]
  const result = new Map<string, any>()

  sections.forEach(({ name, data, id }, index) => {
    if (data.length) {
      result.set(name, { checkoutNumber: index + 1, data, id })
    }
  })

  return {
    review: result.get('review'),
    experienceTransfer: result.get('experienceTransfer'),
    experienceLounge: result.get('experienceLounge'),
    insurance: result.get('insurance'),
    bundledCarHire: result.get('bundledCarHire'),
    travellerDetails: result.get('travellerDetails'),
    arrivalDetails: result.get('arrivalDetails'),
    payment: result.get('payment'),
    merchandise: result.get('merchandise'),
  }
}

const isLuxPlusSubscriptionInFlowCheckout = createSelector(
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => isStandaloneLuxPlusSubscription(state),
  (state: App.State) => getCanRedeemLuxPlusBenefits(state),
  (state: App.State) => getCheckoutBenefitsView(state),
  (isLuxPlusEnabled, isStandaloneLuxPlusCheckout, canRedeemLuxPlusBenefits, benefitsView): boolean => {
    return isLuxPlusEnabled &&
      !isStandaloneLuxPlusCheckout &&
      !canRedeemLuxPlusBenefits &&
      !!benefitsView.data
  },
)

const getRequiresLoyaltySection = createSelector(
  (state: App.State) => getIsLoyaltyEnabled(state),
  (state: App.State) => getCanRedeemCheckoutLoyaltyBenefits(state),
  (state: App.State) => getHasLoyaltyAccount(state),
  (isEnabled, canRedeem, hasAccount): boolean => {
    // we show benefits if they can redeem, but if they don't have account we show a sign up section
    return isEnabled && (canRedeem || !hasAccount)
  },
)

const isLuxPlusSubscriptionStandaloneCheckout = createSelector(
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => isStandaloneLuxPlusSubscription(state),
  (state: App.State) => getCanRedeemLuxPlusBenefits(state),
  (isLuxPlusEnabled, isStandaloneLuxPlusCheckout, canRedeemLuxPlusBenefits): boolean =>
    isLuxPlusEnabled && isStandaloneLuxPlusCheckout && !canRedeemLuxPlusBenefits,
)

const getReviewCheckoutPageSection = createSelector(
  (state: App.State) => getCustomOfferItems(state),
  (state: App.State) => accommodationViews(state),
  (state: App.State) => getVillaViews(state),
  (state: App.State) => getBundleAndSaveItems(state),
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => getCarHireItemsView(state),
  (state: App.State) => getFlightItemsView(state),
  (state: App.State) => getExperienceItemsView(state),
  (state: App.State) => getTransferItems(state),
  (state: App.State) => getGiftCardItems(state),
  (state: App.State) => getPurchaseCreditItems(state),
  (state: App.State) => getTourV2ExperienceItemsViews(state),
  (state: App.State) => isSimpleStandaloneCheckout(state),
  (state: App.State) => isLuxPlusAvailableToAddToCart(state),
  (state: App.State) => isLuxPlusSubscriptionInFlowCheckout(state),
  (state: App.State) => isLuxPlusSubscriptionStandaloneCheckout(state),
  (state: App.State) => isOneClickExperienceTransferEnabled(state),
  (state: App.State) => getRequiresLoyaltySection(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getFlightLoungePassExperienceAvailable(state),
  (state: App.State) => getFunnelFeatures(state),
  (state: App.State) => getIsInternationalCheckoutFlowTreatment(state),
  (
    customOfferItems,
    accommodationViews,
    villaViews,
    bundleAndSaveItems,
    checkoutAccommodationOfferView,
    carHireItemsView,
    flightItemsView,
    experienceViews,
    transferViews,
    giftCardItems,
    purchaseCreditItems,
    tourV2ExperienceItemsViews,
    isSimpleStandaloneCheckout,
    isLuxPlusAvailableToAddToCart,
    isLuxPlusSubscriptionInFlowCheckout,
    isLuxPlusSubscriptionStandaloneCheckout,
    oneClickAirportTransfersABTestEnabled,
    hasAnyLoyaltyBenefitsToRedeem,
    postPurchase,
    flightLoungePassExperienceAvailable,
    funnelFeatures,
    isInternationalCheckoutFlowTreatment,
  ): Array<CheckoutSectionsReview> => {
    const shouldShowExperienceUpsells = funnelFeatures.experiences && !isInternationalCheckoutFlowTreatment
    const sections: Array<[CheckoutSectionsReview, boolean]> = [
      ['luxPlusInlineToggle', funnelFeatures.luxPlus && isInternationalCheckoutFlowTreatment && isLuxPlusAvailableToAddToCart],
      ['customOffer', !!customOfferItems.length],
      ['accommodation', !!accommodationViews.length],
    ]
    if (checkoutAccommodationOfferView.data?.some((view: App.Checkout.AccommodationOfferView) => view.isBundled)) {
      sections.unshift(
        ['tour', !!checkoutAccommodationOfferView.data?.length],
        ['tourExperience', shouldShowExperienceUpsells && !!tourV2ExperienceItemsViews.data.length],
      )
    } else {
      sections.push(
        ['tour', !!checkoutAccommodationOfferView.data?.length],
        ['tourExperience', shouldShowExperienceUpsells && !!tourV2ExperienceItemsViews.data.length],
      )
    }
    const hasStandaloneExperience = experienceViews.data.some(view => !view.item.isAccomBundle)

    sections.push(
      ['villa', !!villaViews.data.length],
      ['bundleAndSave', !!bundleAndSaveItems.length],
      ['cruise', !!checkoutAccommodationOfferView.data.filter(isCruiseOfferView)?.length],
      ['carHire', !!carHireItemsView.data.length],
      ['flight', !!flightItemsView.data.length && postPurchase !== 'flight-change-dates'],
      ['flightDateChange', !!flightItemsView.data.length && postPurchase === 'flight-change-dates'],
      ['experience', shouldShowExperienceUpsells && !flightLoungePassExperienceAvailable && hasStandaloneExperience],
      ['reviewTransfer', !oneClickAirportTransfersABTestEnabled && !!transferViews.filter(t => !t.transfer.isHomePickup).length],
      ['giftCard', !!giftCardItems.length],
      ['purchaseCredit', !!purchaseCreditItems.length],
      ['customerServicePanel', (!!accommodationViews.length || isSimpleStandaloneCheckout) && !config.agentHub.isEnabled && !isLuxPlusSubscriptionStandaloneCheckout],
      ['luxPlusInflow', funnelFeatures.luxPlus && !isInternationalCheckoutFlowTreatment && isLuxPlusSubscriptionInFlowCheckout],
      ['luxPlusReconsider', funnelFeatures.luxPlus && !isInternationalCheckoutFlowTreatment && isLuxPlusSubscriptionInFlowCheckout],
      ['luxPlusStandalone', !isInternationalCheckoutFlowTreatment && isLuxPlusSubscriptionStandaloneCheckout],
      ['loyalty', hasAnyLoyaltyBenefitsToRedeem],
      ['charterUpgrade', postPurchase === 'charter-upgrade'],
    )

    return activeSections(sections)
  },
)

const getExperienceTransferCheckoutPageSection = createSelector(
  (state: App.State) => getOneClickExperienceTransfer(state),
  (state: App.State) => state.experience.experienceTransferOptions,
  (state: App.State) => getTransferItems(state),
  (state: App.State) => isOneClickExperienceTransferEnabled(state),
  (state: App.State) => getExperienceOneClickTransferOptions(state),
  (state: App.State) => getUnifiedTransferView(state),
  (state: App.State) => getIsOneClickTransferAvailable(state),
  (state: App.State) => getFunnelFeatures(state),
  (state: App.State) => getIsInternationalCheckoutFlowTreatment(state),
  (
    oneClickTransfer,
    allTransferOptions,
    checkoutTransferItems,
    oneClickAirportTransfersABTestEnabled,
    onClickTransferOptions,
    unifiedTransferView,
    isAvailableTransfers,
    funnelFeatures,
    isInternationalCheckoutFlowTreatment,
  ): Array<CheckoutSectionsExperienceTransfer> => {
    if (!funnelFeatures.experiences || isInternationalCheckoutFlowTreatment) {
      return activeSections([
        ['experienceTransfer', false],
      ])
    }

    // Hide airport transfers when loyalty transfers are active, but keep the it visible for home-to-airport so users can add more
    const hasLoyaltyTransfers =
      (unifiedTransferView.hasRequiredData &&
        unifiedTransferView.data.transferItems.length > 0 &&
        unifiedTransferView.data.transferType !== 'HOME_TO_AIRPORT')
    // Hide this section if loyalty transfers are present in the cart and no one click transfer is selected
    if (!oneClickAirportTransfersABTestEnabled || (!oneClickTransfer && !checkoutTransferItems.length) || !isAvailableTransfers || hasLoyaltyTransfers) {
      return activeSections([
        ['experienceTransfer', false],
      ])
    }

    const transferId = isNonEmptyArray(checkoutTransferItems) ? checkoutTransferItems[0].experienceId : oneClickTransfer?.id

    // Some transfer options don't include the transfer type, making it unclear whether they are to the hotel or to the airport.
    // We want to display one-click transfers only when the transfer type is known, as this information must be shown to the customer.
    const isOptionWithTransferType = transferId ? !!allTransferOptions[transferId]?.transfers?.some(transferOption => !!transferOption?.transferTypes?.length) : false

    return activeSections([
      ['experienceTransfer', isOptionWithTransferType],
    ])
  })

const getExperienceLoungeCheckoutPageSection = createSelector(
  (state: App.State) => getFlightLoungePassExperienceAvailable(state),
  (state: App.State) => getFunnelFeatures(state),
  (state: App.State) => getIsInternationalCheckoutFlowTreatment(state),
  (state: App.State) => hasBundledAccommodationRate(state),
  (flightLoungePassExperienceAvailable, funnelFeatures, isInternationalCheckoutFlowTreatment, hasBundledAccommodationRate): Array<CheckoutSectionsExperienceLounge> => {
    return activeSections([
      ['experienceLounge', !isInternationalCheckoutFlowTreatment && funnelFeatures.experiences && !hasBundledAccommodationRate && flightLoungePassExperienceAvailable],
    ])
  },
)

// Show spoofed insurance section when spoofing is enabled and either insurance or booking protection are available
export const getShouldShowSpoofedInsuranceSection = createSelector(
  (state: App.State) => isSpoofed(state),
  (state: App.State) => isInsuranceSupported(state),
  (state: App.State) => checkoutShouldShowBookingProtection(state),
  (isSpoofed, insuranceSupported, isBookingProtectionSupported): boolean => {
    return isSpoofed && (insuranceSupported || isBookingProtectionSupported)
  },
)

// Show regular insurance when:
// - Not spoofed
// - Insurance is supported
// - No flights with embedded insurance
// - Either no booking protection OR standalone flights (which get their own booking protection)
export const getShouldShowRegularInsuranceSection = createSelector(
  (state: App.State) => isSpoofed(state),
  (state: App.State) => isPassportCardExclusiveCheckoutEnabled(state),
  (state: App.State) => isInsuranceSupported(state),
  (state: App.State) => checkoutContainsFlightsWithEmbeddedInsurance(state),
  (state: App.State) => checkoutShouldShowBookingProtection(state),
  (state: App.State) => isStandaloneFlights(state),
  (
    isSpoofed,
    shouldShowPassportCardInsurance,
    insuranceSupported,
    checkoutContainsFlightsWithEmbeddedInsurance,
    isBookingProtectionSupported,
    isStandaloneFlights,
  ): boolean => {
    return !isSpoofed &&
      !shouldShowPassportCardInsurance &&
      insuranceSupported &&
      !checkoutContainsFlightsWithEmbeddedInsurance &&
      (!isBookingProtectionSupported || isStandaloneFlights)
  },
)

// Show booking protection when:
// - Not spoofed
// - Booking protection should be shown
// - Not standalone flights (they use regular insurance instead)
// - Either no embedded insurance OR (not standalone flights AND has embedded insurance)
export const getShouldShowBookingProtectionSection = createSelector(
  (state: App.State) => isSpoofed(state),
  (state: App.State) => isPassportCardExclusiveCheckoutEnabled(state),
  (state: App.State) => checkoutShouldShowBookingProtection(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => checkoutContainsFlightsWithEmbeddedInsurance(state),
  (
    isSpoofed,
    shouldShowPassportCardInsurance,
    isBookingProtectionSupported,
    isStandaloneFlights,
    checkoutContainsFlightsWithEmbeddedInsurance,
  ): boolean => {
    return !isSpoofed &&
      !shouldShowPassportCardInsurance &&
      isBookingProtectionSupported &&
      !isStandaloneFlights &&
      (!checkoutContainsFlightsWithEmbeddedInsurance ||
        (!isStandaloneFlights && checkoutContainsFlightsWithEmbeddedInsurance))
  },
)

const getInsuranceCheckoutPageSection = createSelector(
  (state: App.State) => isPassportCardExclusiveCheckoutEnabled(state),
  (state: App.State) => getShouldShowSpoofedInsuranceSection(state),
  (state: App.State) => getShouldShowRegularInsuranceSection(state),
  (state: App.State) => getShouldShowBookingProtectionSection(state),
  (state: App.State) => getFunnelFeatures(state),
  (state: App.State) => getIsInternationalCheckoutFlowTreatment(state),
  (showPassportCardInsurance, showSpoofedInsurance, showRegularInsurance, showBookingProtection, funnelFeatures, isInternationalCheckoutFlowTreatment): Array<CheckoutSectionsInsurance> => {
    if (isInternationalCheckoutFlowTreatment) {
      return activeSections([
        ['passportCardInsurance', false],
        ['spoofedInsurance', false],
        ['insurance', false],
        ['bookingProtection', false],
      ])
    }

    // Spoofed insurance shows both CFMR and travel protection in spoof mode
    // It is gated by both flags - if either is disabled, spoofed section is hidden
    const spoofedEnabled = funnelFeatures.cfmr && funnelFeatures.travelProtection
    return activeSections([
      ['passportCardInsurance', showPassportCardInsurance],
      ['spoofedInsurance', showSpoofedInsurance && spoofedEnabled],
      ['insurance', showRegularInsurance && funnelFeatures.travelProtection],
      ['bookingProtection', showBookingProtection && funnelFeatures.cfmr],
    ])
  },
)

const getBundledCarHireCheckoutPageSection = createSelector(
  (state: App.State) => isBundledCarHireEnabled(state),
  (state: App.State) => isCartEligibleForBundledCarHire(state),
  (state: App.State) => getFunnelFeatures(state),
  (isBundledCarHireEnabled, isCartEligibleForBundledCarHire, funnelFeatures): Array<CheckoutSectionsCarHire> => {
    return activeSections([
      ['bundledCarHire', isBundledCarHireEnabled && isCartEligibleForBundledCarHire && funnelFeatures.carHire],
    ])
  },
)

const getTravellerCheckoutPageSection = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => shouldShowTravellerForm(state),
  (state: App.State) => checkoutShouldShowCarHireArrivalInput(state),
  (state: App.State) => state.checkout.cart.mode,
  (state: App.State) => cartIncludesFlights(state),
  (state: App.State) => getFlightItems(state),
  (
    existingOrder,
    shouldShowTravellerForm,
    shouldShowCarHireArrivalInput,
    cartMode,
    hasFlightsInCart,
    flightItems,
  ): Array<CheckoutSectionsTravellerDetails> => {
    // Don't show traveller section at all for payment-schedule-balance mode && flight-change-dates mode
    if (['payment-schedule-balance', 'deposit-balance', 'flight-change-dates', 'charter-upgrade'].includes(cartMode ?? '')) {
      return []
    }

    // Only show flight services after final fares are confirmed to prevent race condition with seat fetching API
    // need to check the final fares for all flight items that have flights available as feeder flights can be skipped in-case of charter
    const hasFlightsFinalFares = flightItems?.length > 0
      && flightItems?.filter(item => item.flights.length > 0)
        .every(item => item.hasFinalFare)

    // array of tuples as the order matters
    return activeSections([
      ['travellerDetails', shouldShowTravellerForm],
      ['flightServices', hasFlightsInCart && hasFlightsFinalFares && config.CHECKOUT_FLIGHT_SERVICES_ENABLED],
      ['carHireArrivalFlightNumber', shouldShowCarHireArrivalInput],
      ['agentBookingDetails', config.agentHub.isEnabled],
      ['depositDetailsNotice', !!existingOrder?.depositDetails && isBalancePending(existingOrder.depositDetails.deposit_status)],
    ])
  },
)

const getArrivalDetailsCheckoutPageSection = createSelector(
  (state: App.State) => shouldShowArrivalDetailsForm(state),
  (
    shouldShowArrivalDetailsForm,
  ): Array<CheckoutSectionsArrivalDetails> => {
    return activeSections([
      ['arrivalDetails', shouldShowArrivalDetailsForm],
    ])
  })

const getPaymentCheckoutPageSection = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => shouldShowPaymentForm(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (
    existingOrder,
    showPaymentForm,
    postPurchase,
  ): Array<CheckoutSectionsPayment> => {
    // array of tuples as the order matters
    return activeSections([
      ['instalmentBalancePaymentSchedule', (postPurchase === 'instalment' || postPurchase === 'instalment-balance') && !!existingOrder?.instalmentDetails],
      ['paymentForm', showPaymentForm],
    ])
  },
)

const getMerchandiseCheckoutPageSection = createSelector(
  (state: App.State) => getMerchandiseItems(state),
  (merchandiseItems): Array<CheckoutSectionsMerchandise> => {
    return activeSections([
      ['merchandise', !!merchandiseItems.length],
      ['merchandiseShipping', !!merchandiseItems.length],
    ])
  },
)

const getPaymentSectionTitle = createSelector(
  (state: App.State) => getCheckoutTotal(state),
  (total): CheckoutSectionTitle | undefined => {
    return total > 0 ? { kind: 'descriptor', descriptor: sectionTitleMessages.paymentHow } : undefined
  },
)

export const getCheckoutPageSections = createSelector(
  (state: App.State) => getReviewCheckoutPageSection(state),
  (state: App.State) => getExperienceTransferCheckoutPageSection(state),
  (state: App.State) => getExperienceLoungeCheckoutPageSection(state),
  (state: App.State) => getInsuranceCheckoutPageSection(state),
  (state: App.State) => getBundledCarHireCheckoutPageSection(state),
  (state: App.State) => getTravellerCheckoutPageSection(state),
  (state: App.State) => getArrivalDetailsCheckoutPageSection(state),
  (state: App.State) => getPaymentCheckoutPageSection(state),
  (state: App.State) => getMerchandiseCheckoutPageSection(state),
  (state: App.State) => getPaymentSectionTitle(state),
  (state: App.State) => getReviewSectionTitle(state),
  (state: App.State) => getTravellerSectionTitle(state),
  (state: App.State) => getInsuranceSectionTitle(state),
  (state: App.State) => getBundledCarHireSectionTitle(state),
  (
    reviewSections,
    experienceTransferSection,
    experienceLoungeSection,
    insuranceSections,
    bundledCarHireSection,
    travellerSections,
    arrivalDetails,
    paymentSections,
    merchandiseSections,
    paymentSectionTitle,
    reviewSectionTitle,
    travellerSectionTitle,
    insuranceSectionTitle,
    bundledCarHireSectionTitle,
  ): CheckoutSections => {
    const numberedSections = getNumberedSections({
      reviewSections,
      experienceTransferSection,
      experienceLoungeSection,
      insuranceSections,
      bundledCarHireSection,
      travellerSections,
      arrivalDetails,
      paymentSections,
      merchandiseSections,
    })

    return {
      review: { title: reviewSectionTitle, ...numberedSections.review },
      experienceTransfer: { title: { kind: 'descriptor', descriptor: sectionTitleMessages.experienceTransfer }, ...numberedSections.experienceTransfer },
      experienceLounge: { title: { kind: 'descriptor', descriptor: sectionTitleMessages.experienceLounge }, ...numberedSections.experienceLounge },
      insurance: { title: insuranceSectionTitle, ...numberedSections.insurance },
      bundledCarHire: { title: bundledCarHireSectionTitle, ...numberedSections.bundledCarHire },
      travellerDetails: { title: travellerSectionTitle, ...numberedSections.travellerDetails },
      arrivalDetails: { title: { kind: 'descriptor', descriptor: sectionTitleMessages.arrivalDetails }, ...numberedSections.arrivalDetails },
      payment: { title: paymentSectionTitle, ...numberedSections.payment },
      merchandise: { title: { kind: 'descriptor', descriptor: sectionTitleMessages.merchandiseReview }, ...numberedSections.merchandise },
    }
  },
)

export const getHasRentalInCart = createSelector(
  (state: App.State) => state?.checkout?.cart?.items,
  (items) => items?.some(item => item.itemType === 'villa') ?? false,
)
